import { Decision, actorDB, hasTag, hasTagOverlap, } from "./const.js";
import { Direction } from "./helpers.js";
import { Layer } from "./tile.js";
import { isWired, WireOverlapMode } from "./wires.js";
/**
 * Current state of sliding, playables can escape weak sliding.
 */
export var SlidingState;
(function (SlidingState) {
    SlidingState[SlidingState["NONE"] = 0] = "NONE";
    // Force floors
    SlidingState[SlidingState["WEAK"] = 1] = "WEAK";
    // Ice
    SlidingState[SlidingState["STRONG"] = 2] = "STRONG";
})(SlidingState || (SlidingState = {}));
/**
 * Checks if a tag collections matches a tag rule of another tag collection.
 * @param actorTags The tag collection the actor has, they are being tested against the rules, cannot have tags which start with "!"
 * @param ruleTags The rules which the actor tags are tested for. A rule is considered fulfilled if
 *
 * a. The rule tag does not start with "!" and is present in the actor tag collection
 *
 * b. The rule tag starts with "!", and the actor tag collection does not contain the rule tag (with the "!" removed)
 * @example ```js
 * matchTags(["foo", "bar"], ["bar", "baz"]) // true
 * matchTags(["foo", "baz"], ["!foo"]) // false
 * matchTags(["foo", "bar"], ["!foo", "bar"]) // true
 * ```
 */
export function matchTags(actorTags, ruleTags) {
    return !!ruleTags.find(val => val.startsWith("!")
        ? !actorTags.includes(val.substr(1))
        : actorTags.includes(val));
}
export const tagProperties = [
    "pushTags",
    "tags",
    "blockTags",
    "blockedByTags",
    "collisionIgnoreTags",
    "ignoreTags",
    "immuneTags",
];
export class Actor {
    hasTag(tag) {
        return hasTag(this, tag);
    }
    calcTag(prop, items) {
        var _a;
        let initTags = (_a = Object.getPrototypeOf(this).constructor[prop]) !== null && _a !== void 0 ? _a : BigInt(0);
        if (items) {
            initTags |= this.calcItemTags(prop);
        }
        return initTags;
    }
    calcItemTags(prop) {
        var _a;
        let tags = BigInt(0);
        for (const item of this.inventory.items) {
            const carrierTags = (_a = item.carrierTags) === null || _a === void 0 ? void 0 : _a[prop];
            tags |= carrierTags !== null && carrierTags !== void 0 ? carrierTags : BigInt(0);
        }
        return tags;
    }
    recomputeTags(items = true) {
        for (const prop of tagProperties) {
            this[prop] = this.calcTag(prop, items);
        }
    }
    _internalIgnores(other) {
        var _a, _b, _c;
        return ((_c = (hasTagOverlap(this.tags, other.ignoreTags) ||
            hasTagOverlap(other.tags, this.ignoreTags) ||
            ((_a = this.ignores) === null || _a === void 0 ? void 0 : _a.call(this, other)) ||
            ((_b = other.ignores) === null || _b === void 0 ? void 0 : _b.call(other, this)))) !== null && _c !== void 0 ? _c : false);
    }
    dropItemN(id, noSideEffect = false) {
        var _a;
        if (this.level.cc1Boots)
            return false;
        const itemToDrop = this.inventory.items[id];
        if (!itemToDrop)
            return false;
        if (this.despawned) {
            console.warn("Dropping items while despawned in undefined behaviour.");
        }
        if (this.tile[itemToDrop.layer])
            return false;
        if (itemToDrop.canBeDropped && !itemToDrop.canBeDropped(this))
            return false;
        this.inventory.items.splice(id, 1);
        itemToDrop.oldTile = null;
        itemToDrop.tile = this.tile;
        this.level.actors.push(itemToDrop);
        itemToDrop._internalUpdateTileStates();
        if (!noSideEffect)
            (_a = itemToDrop.onDrop) === null || _a === void 0 ? void 0 : _a.call(itemToDrop, this);
        itemToDrop.exists = true;
        if (itemToDrop.carrierTags) {
            for (const prop in itemToDrop.carrierTags) {
                this[prop] = this.calcTag(prop, true);
            }
        }
        return true;
    }
    dropItem() {
        return this.dropItemN(this.inventory.items.length - 1);
    }
    constructor(level, position, customData = "", direction = Direction.UP) {
        var _a, _b, _c;
        this.level = level;
        this.customData = customData;
        this.direction = direction;
        this.moveDecision = Decision.NONE;
        this.currentMoveSpeed = null;
        this.oldTile = null;
        this.cooldown = 0;
        this.pendingDecision = Decision.NONE;
        this.pendingDecisionLockedIn = false;
        this.slidingState = SlidingState.NONE;
        this.despawned = false;
        this.exists = true;
        this.isDeciding = false;
        this.isPulled = false;
        /**
         * Tags which the actor can push, provided the pushed actor can be pushed
         */
        this.pushTags = BigInt(0);
        /**
         * General-use tags to use, for example, for collisions
         */
        this.tags = BigInt(0);
        /**
         * Tags which this actor blocks
         */
        this.blockTags = BigInt(0);
        /**
         * Tags which this actor is blocked by
         */
        this.blockedByTags = BigInt(0);
        /**
         * Tags which this actor refuses to be blocked by
         */
        this.collisionIgnoreTags = BigInt(0);
        /**
         * Tags which this actor will not conduct any interactions with.
         */
        this.ignoreTags = BigInt(0);
        /**
         * Tags which this actor should not be destroyed by
         */
        this.immuneTags = BigInt(0);
        /**
         * Amount of ticks it takes for the actor to move
         */
        this.moveSpeed = 4;
        this.inventory = {
            items: [],
            keys: {},
            itemMax: 4,
        };
        /**
         * True if the last move failed
         */
        this.bonked = false;
        this.isPushing = false;
        /**
         * The colors of buttons this actor cares about
         */
        this.caresButtonColors = [];
        this.wires = 0;
        /**
         * The currently powered wires, either by it's own abilities of via neighbors
         */
        this.poweredWires = 0;
        /**
         * The wires this actor is powering itself
         */
        this.poweringWires = 0;
        this.wireTunnels = 0;
        this.wireOverlapMode = WireOverlapMode.NONE;
        this.wired = false;
        /**
         * If true, the actor can't move at all
         */
        this.frozen = false;
        this.layer = this.getLayer();
        for (const tagProp of tagProperties.concat((_a = new.target.extraTagProperties) !== null && _a !== void 0 ? _a : [])) {
            this[tagProp] = (_b = new.target[tagProp]) !== null && _b !== void 0 ? _b : BigInt(0);
        }
        level.actors.unshift(this);
        this.tile = level.field[position[0]][position[1]];
        this.tile.addActors(this);
        this.isDeciding = !!(this.layer === Layer.MOVABLE ||
            this.onEachDecision ||
            this.decideMovement);
        if (this.isDeciding)
            level.decidingActors.unshift(this);
        this.createdN = this.level.createdN++;
        if (this.level.levelStarted) {
            (_c = this.onCreation) === null || _c === void 0 ? void 0 : _c.call(this);
            this.wired = isWired(this);
        }
    }
    _internalDecide(forcedOnly = false) {
        var _a, _b;
        if (!this.exists)
            return;
        this.bonked = false;
        this.moveDecision = Decision.NONE;
        if (this.cooldown || this.frozen)
            return; // This is where the decision *actually* begins
        this.currentMoveSpeed = null;
        this.isPushing = false;
        if (this.pendingDecision) {
            this.moveDecision = this.pendingDecision;
            this.pendingDecision = Decision.NONE;
            this.pendingDecisionLockedIn = true;
            return;
        }
        // This is where the decision *actually* begins		// Since this is a generic actor, we cannot override weak sliding
        // TODO Ghost ice shenanigans
        if (this.slidingState) {
            this.moveDecision = this.direction + 1;
            return;
        }
        (_a = this.onEachDecision) === null || _a === void 0 ? void 0 : _a.call(this, forcedOnly);
        if (forcedOnly)
            return;
        const directions = (_b = this.decideMovement) === null || _b === void 0 ? void 0 : _b.call(this);
        if (!directions || directions.length === 0)
            return;
        for (const direction of directions)
            if (this.checkCollision(direction)) {
                // Yeah, we can go here
                this.moveDecision = direction + 1;
                return;
            }
        // Force last decision if all other fail
        this.moveDecision = directions[directions.length - 1] + 1;
    }
    selfSpeedMod(mult) {
        for (const item of this.inventory.items) {
            if (item.carrierSpeedMod)
                mult *= item.carrierSpeedMod(this, mult);
        }
        return mult;
    }
    _internalStep(direction) {
        if (this.cooldown || !this.moveSpeed)
            return false;
        this.direction = direction;
        const canMove = this.checkCollision(direction);
        this.bonked = !canMove;
        this.direction = this.level.resolvedCollisionCheckDirection;
        // Welp, something stole our spot, too bad
        if (!canMove)
            return false;
        if (!this.isDeciding)
            this.level.decidingActors.push(this);
        this.isDeciding = true;
        const newTile = this.tile.getNeighbor(this.level.resolvedCollisionCheckDirection, false);
        this.pendingDecision = Decision.NONE;
        this.moveDecision = Decision.NONE;
        // This is purely a defensive programming thing, shouldn't happen normally (checkCollision should check for going OOB)
        if (!newTile)
            return false;
        let speedMult = 1;
        speedMult = newTile.getSpeedMod(this);
        speedMult = this.selfSpeedMod(speedMult);
        const moveLength = (this.moveSpeed * 3) / speedMult;
        this.currentMoveSpeed = this.cooldown = moveLength;
        this.oldTile = this.tile;
        this.tile = newTile;
        // Finally, move ourselves to the new tile
        this._internalUpdateTileStates();
        return true;
    }
    _internalMove() {
        if (!this.exists)
            return;
        if (this.cooldown > 0) {
            this.isPulled = false;
            this.moveDecision = Decision.NONE;
            return;
        }
        if (!this.moveDecision) {
            this.isPulled = false;
            return;
        }
        this.pendingDecision = Decision.NONE;
        this.pendingDecisionLockedIn = false;
        const ogDirection = this.moveDecision - 1;
        const success = this._internalStep(ogDirection);
        this.isPulled = false;
    }
    _internalShouldDie(killer) {
        return !(this._internalIgnores(killer) ||
            hasTagOverlap(killer.tags, this.immuneTags) ||
            (this.shouldDie && !this.shouldDie(killer)));
    }
    _internalCollisionIgnores(other, direction) {
        var _a;
        return !!(hasTagOverlap(this.tags, other.collisionIgnoreTags) ||
            ((_a = other.collisionIgnores) === null || _a === void 0 ? void 0 : _a.call(other, this, direction)));
    }
    _internalBlocks(other, moveDirection) {
        var _a, _b;
        return (
        // A hack for teleports, but it's not that dumb of a limitation, so maybe it's fine?
        other !== this &&
            // FIXME A hack for blocks, really shouldn't be a forced limitation
            (!!(this.cooldown && this.moveSpeed) ||
                (!this._internalCollisionIgnores(other, moveDirection) &&
                    (((_a = this.blocks) === null || _a === void 0 ? void 0 : _a.call(this, other, moveDirection)) ||
                        ((_b = other.blockedBy) === null || _b === void 0 ? void 0 : _b.call(other, this, moveDirection)) ||
                        hasTagOverlap(other.tags, this.blockTags) ||
                        hasTagOverlap(this.tags, other.blockedByTags)))));
    }
    enterTile(noOnTile = false) {
        var _a, _b, _c, _d, _e;
        let thisActor = this;
        if (thisActor.oldTile) {
            for (const actor of thisActor.oldTile.allActorsReverse) {
                if (!thisActor._internalIgnores(actor)) {
                    (_a = actor.actorCompletelyLeft) === null || _a === void 0 ? void 0 : _a.call(actor, thisActor);
                    if (thisActor.newActor)
                        thisActor = thisActor.newActor;
                }
            }
        }
        for (const actor of thisActor.tile.allActorsReverse) {
            if (actor === thisActor)
                continue;
            const notIgnores = !thisActor._internalIgnores(actor);
            if (notIgnores)
                (_b = actor.actorCompletelyJoined) === null || _b === void 0 ? void 0 : _b.call(actor, thisActor);
            else
                (_c = actor.actorCompletelyJoinedIgnored) === null || _c === void 0 ? void 0 : _c.call(actor, thisActor);
            if (thisActor.newActor)
                thisActor = thisActor.newActor;
            if (!noOnTile && actor.actorOnTile) {
                actor.actorOnTile(thisActor);
            }
            if (thisActor.newActor)
                thisActor = thisActor.newActor;
        }
        if (this.exists) {
            (_d = this.newTileCompletelyJoined) === null || _d === void 0 ? void 0 : _d.call(this);
            for (const item of this.inventory.items)
                (_e = item.onCarrierCompleteJoin) === null || _e === void 0 ? void 0 : _e.call(item, this);
        }
        this.cooldown = 0;
    }
    _internalDoCooldown() {
        if (!this.exists)
            return;
        if (this.cooldown > 0 && this.cooldown <= 1) {
            if (this.pendingDecision)
                this.pendingDecisionLockedIn = true;
            this.enterTile();
        }
        else if (this.cooldown > 0)
            this.cooldown--;
        else if (this.exists) {
            let thisActor = this;
            for (const actor of [...this.tile.allActors])
                if (actor !== thisActor && actor.actorOnTile) {
                    actor.actorOnTile(thisActor);
                    if (thisActor.newActor)
                        thisActor = thisActor.newActor;
                }
        }
        this.bonked = false;
    }
    /**
     * Checks if a specific actor can move in a certain direction
     * @param actor The actor to check for
     * @param direction The direction the actor wants to move in
     * @param pushBlocks If true, it will push blocks
     * @returns If the actor *can* move in that direction
     */
    checkCollision(direction, redirectOnly = false, pull = true) {
        return this.checkCollisionFromTile(this.tile, direction, redirectOnly, pull);
    }
    /**
     * Checks if a specific actor can move in a certain direction to a certain tile
     * @param this The actor to check for
     * @param direction The direction the actor wants to enter the tile
     * @param fromTile The tile the actor is coming from
     * @param pushBlocks If true, it will push blocks
     * @returns If the actor *can* move in that direction
     */
    checkCollisionFromTile(fromTile, direction, redirectOnly = false, pull = true) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        // This is a pass by reference-esque thing, please don't die of cring
        this.level.resolvedCollisionCheckDirection = direction;
        // Do stuff on the leaving tile
        for (const exitActor of fromTile.allActorsReverse)
            if (!redirectOnly && exitActor._internalExitBlocks(this, direction)) {
                (_a = exitActor.bumped) === null || _a === void 0 ? void 0 : _a.call(exitActor, this, direction);
                (_b = this.bumpedActor) === null || _b === void 0 ? void 0 : _b.call(this, exitActor, direction, true);
                return false;
            }
            else {
                if (!exitActor.redirectTileMemberDirection ||
                    this._internalIgnores(exitActor))
                    continue;
                const redirection = exitActor.redirectTileMemberDirection(this, direction);
                if (redirection === null)
                    return false;
                (_c = this.onRedirect) === null || _c === void 0 ? void 0 : _c.call(this, (redirection - direction + 4) % 4);
                this.level.resolvedCollisionCheckDirection = direction = redirection;
            }
        if (redirectOnly)
            return true;
        const newTile = fromTile.getNeighbor(direction, false);
        if (newTile === null) {
            (_d = this.bumpedEdge) === null || _d === void 0 ? void 0 : _d.call(this, fromTile, direction);
            return false;
        }
        const toPush = [];
        // Do stuff on the entering tile
        loop: for (const layer of [
            Layer.ITEM_SUFFIX,
            Layer.SPECIAL,
            Layer.STATIONARY,
            Layer.MOVABLE,
            Layer.ITEM,
        ]) {
            let blockActor = newTile[layer];
            if (!blockActor)
                continue;
            for (const item of this.inventory.items) {
                (_e = item.onCarrierBump) === null || _e === void 0 ? void 0 : _e.call(item, this, blockActor, direction);
                if (blockActor.newActor)
                    blockActor = blockActor.newActor;
            }
            (_f = blockActor.bumped) === null || _f === void 0 ? void 0 : _f.call(blockActor, this, direction);
            (_g = this.bumpedActor) === null || _g === void 0 ? void 0 : _g.call(this, blockActor, direction, false);
            if (blockActor._internalBlocks(this, direction))
                if (this._internalCanPush(blockActor, direction))
                    toPush.push(blockActor);
                else {
                    this.level.resolvedCollisionCheckDirection = direction;
                    return false;
                }
            if (layer === Layer.MOVABLE)
                // This is dumb
                break loop;
        }
        for (const pushable of toPush) {
            this.level.resolvedCollisionCheckDirection = direction;
            if (pushable.slidingState) {
                // Blocks with no cooldown can't have their pending decision be overriden
                if (!pushable.pendingDecisionLockedIn) {
                    pushable.pendingDecision = pushable.moveDecision = direction + 1;
                }
                return false;
            }
            if (pushable.cooldown || !pushable.checkCollision(direction)) {
                this.level.resolvedCollisionCheckDirection = direction;
                return false;
            }
            if (pushable._internalStep(direction)) {
                pushable.cooldown--;
                if (this.hasTag("plays-block-push-sfx"))
                    (_h = this.level.sfxManager) === null || _h === void 0 ? void 0 : _h.playOnce("block push");
            }
        }
        this.level.resolvedCollisionCheckDirection = direction;
        if (toPush.length !== 0)
            this.isPushing = true;
        if (pull && this.hasTag("pulling")) {
            const backTile = this.tile.getNeighbor((direction + 2) % 4);
            if (!backTile)
                return true;
            const pulledActor = backTile[Layer.MOVABLE];
            if (pulledActor) {
                if (pulledActor.cooldown && pulledActor.moveSpeed)
                    return false;
                if ((pulledActor.pendingDecisionLockedIn && pulledActor.isPulled) ||
                    !pulledActor.hasTag("block") ||
                    (pulledActor.canBePushed && !pulledActor.canBePushed(this, direction))) {
                    pulledActor.isPulled = true;
                    return true;
                }
                pulledActor.isPulled = true;
                pulledActor.direction = direction;
                if (pulledActor.frozen)
                    return true;
                pulledActor.pendingDecision = pulledActor.moveDecision = direction + 1;
            }
        }
        return true;
    }
    /**
     * Updates tile states and calls hooks
     */
    _internalUpdateTileStates(noTileRemove) {
        var _a, _b, _c, _d, _e;
        this.respawn(false);
        if (!noTileRemove) {
            (_a = this.oldTile) === null || _a === void 0 ? void 0 : _a.removeActors(this);
            this.slidingState = SlidingState.NONE;
            // Spread from and to to not have actors which create new actors instantly be interacted with
            if (this.oldTile)
                for (const actor of [...this.oldTile.allActorsReverse])
                    if (!this._internalIgnores(actor))
                        (_b = actor.actorLeft) === null || _b === void 0 ? void 0 : _b.call(actor, this);
        }
        this.tile.addActors(this);
        for (const actor of [...this.tile.allActorsReverse])
            if (actor !== this && !this._internalIgnores(actor))
                (_c = actor.actorJoined) === null || _c === void 0 ? void 0 : _c.call(actor, this);
        (_d = this.newTileJoined) === null || _d === void 0 ? void 0 : _d.call(this);
        for (const item of this.inventory.items)
            (_e = item.onCarrierJoin) === null || _e === void 0 ? void 0 : _e.call(item, this);
    }
    respawn(putOnTile = true) {
        if (!this.despawned)
            return;
        this.despawned = false;
        if (this.level.despawnedActors.includes(this))
            this.level.despawnedActors.splice(this.level.despawnedActors.indexOf(this), 1);
        if (putOnTile) {
            this.tile.addActors(this);
        }
    }
    despawn() {
        if (this.despawned)
            return;
        this.level.despawnedActors.push(this);
        this.despawned = true;
        this.tile.removeActors(this);
    }
    destroy(killer, animType = "explosion") {
        var _a, _b;
        if (killer && !this._internalShouldDie(killer))
            return false;
        if (this.level.actors.includes(this))
            this.level.actors.splice(this.level.actors.indexOf(this), 1);
        const decidingPos = this.level.decidingActors.indexOf(this);
        if (this.level.decidingActors.includes(this))
            this.level.decidingActors.splice(this.level.decidingActors.indexOf(this), 1);
        if (this.despawned) {
            if (this.level.despawnedActors.includes(this))
                this.level.despawnedActors.splice(this.level.despawnedActors.indexOf(this), 1);
        }
        if (this.level.circuitInputs.includes(this)) {
            this.level.circuitInputs.splice(this.level.circuitInputs.indexOf(this), 1);
        }
        if (this.level.circuitOutputs.includes(this)) {
            this.level.circuitOutputs.splice(this.level.circuitOutputs.indexOf(this), 1);
        }
        this.tile.removeActors(this);
        this.exists = false;
        if (animType && actorDB[`${animType}Anim`] && !this.tile[Layer.MOVABLE]) {
            const anim = new actorDB[`${animType}Anim`](this.level, this.tile.position);
            if (decidingPos !== -1) {
                this.level.decidingActors.splice(this.level.decidingActors.indexOf(anim), 1);
                this.level.decidingActors.splice(decidingPos, 0, anim);
            }
            anim.direction = this.direction;
            anim.currentMoveSpeed = this.currentMoveSpeed;
            anim.cooldown = this.cooldown;
            anim.inventory = this.inventory;
            this.newActor = anim;
        }
        for (const thing of this.tile.allActors) {
            (_a = thing.actorDestroyed) === null || _a === void 0 ? void 0 : _a.call(thing, this);
        }
        for (const item of this.inventory.items) {
            (_b = item.onCarrierDestroyed) === null || _b === void 0 ? void 0 : _b.call(item, this);
        }
        return true;
    }
    getVisualPosition() {
        if (!this.cooldown || !this.currentMoveSpeed || !this.oldTile)
            return this.tile.position;
        const progress = 1 - this.cooldown / this.currentMoveSpeed;
        return [
            this.oldTile.x * (1 - progress) + this.tile.x * progress,
            this.oldTile.y * (1 - progress) + this.tile.y * progress,
        ];
    }
    _internalCanPush(other, direction) {
        var _a, _b;
        //if (other.pendingDecision) return false
        if (!hasTagOverlap(other.tags, this.pushTags) ||
            !((_b = (_a = other.canBePushed) === null || _a === void 0 ? void 0 : _a.call(other, this, direction)) !== null && _b !== void 0 ? _b : true))
            return false;
        if (other.pendingDecisionLockedIn)
            return false;
        return other.checkCollision(direction, true);
    }
    _internalExitBlocks(other, exitDirection) {
        var _a, _b, _c;
        return ((_c = (!((_a = other.collisionIgnores) === null || _a === void 0 ? void 0 : _a.call(other, this, exitDirection)) &&
            !hasTagOverlap(this.tags, other.collisionIgnoreTags) &&
            ((_b = this.exitBlocks) === null || _b === void 0 ? void 0 : _b.call(this, other, exitDirection)))) !== null && _c !== void 0 ? _c : false);
    }
    replaceWith(other, customData) {
        const decidingPos = this.level.decidingActors.indexOf(this);
        this.destroy(null, null);
        const newActor = new other(this.level, this.tile.position, customData !== null && customData !== void 0 ? customData : this.customData);
        newActor.direction = this.direction;
        newActor.inventory = this.inventory;
        if (newActor.isDeciding && decidingPos !== -1) {
            this.level.decidingActors.splice(this.level.decidingActors.indexOf(newActor), 1);
            this.level.decidingActors.splice(decidingPos, 0, newActor);
        }
        this.newActor = newActor;
        newActor.recomputeTags();
        return newActor;
    }
}
//# sourceMappingURL=actor.js.map